import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, CalendarDays, TrendingUp, UserX } from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";

import { Card, Spinner, StatusBadge } from "@/components/ui";
import { api } from "@/lib/api";
import { useFormat, useT } from "@/lib/i18n";
import { useStats } from "@/lib/queries";
import type { Me } from "@/lib/types";

export default function Home() {
  const { money } = useFormat();
  const t = useT();
  const me = useOutletContext<Me>();
  const { data, isLoading } = useStats();

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      <h1 className="text-xl font-semibold lg:text-2xl">
        {t("home.hello", { nome: me.full_name.split(" ")[0] })}
      </h1>
      <p className="text-sm text-steel-muted">{me.garage.name}</p>

      <OnboardingBanner />

      {me.garage.subscription_status === "trialing" && (
        <Card className="mt-4 border-racing-600/40 bg-racing-500/10 p-4">
          <p
            className="text-sm text-racing-400"
            dangerouslySetInnerHTML={{
              __html: t("home.onTrialOf", { plano: me.garage.plan?.name ?? "" }),
            }}
          />
        </Card>
      )}

      {/* Os três números que o dono de oficina realmente quer ver. */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Stat
          icon={<CalendarDays size={18} />}
          label={t("home.todayAppointments")}
          value={String(data.today.appointments)}
        />
        {me.permissions.see_financials && (
          <>
            <Stat
              icon={<TrendingUp size={18} />}
              label={t("home.todayBilled")}
              value={money(data.today.revenue)}
              hint={t("home.onlyCompleted")}
            />
            <Stat
              icon={<TrendingUp size={18} />}
              label={t("home.monthBilled")}
              value={money(data.month.revenue)}
              hint={t("home.monthAppointments", { n: data.month.appointments })}
            />
          </>
        )}
        <Stat
          icon={<UserX size={18} />}
          label={t("home.monthNoShows")}
          value={String(data.month.no_shows)}
          hint={data.month.no_shows > 0 ? t("home.noShowHint") : undefined}
        />
      </div>

      {Object.keys(data.today.by_status).length > 0 && (
        <Card className="mt-6 p-5">
          <h2 className="mb-3 font-medium">{t("home.todayStatus")}</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.today.by_status).map(([status, n]) => (
              <span key={status} className="inline-flex items-center gap-1">
                <StatusBadge status={status} />
                <span className="text-sm text-steel-dim">{n}</span>
              </span>
            ))}
          </div>
        </Card>
      )}

      <Card className="mt-6 p-5">
        <h2 className="mb-1 font-medium">{t("home.publicPage")}</h2>
        <p className="mb-3 text-sm text-steel-muted">{t("home.publicPageText")}</p>
        <a
          href={me.garage.public_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm font-medium text-racing-500 hover:underline"
        >
          {me.garage.public_url}
          <ArrowUpRight size={14} />
        </a>
      </Card>

      <Link
        to="/agenda"
        className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-racing-500 hover:underline"
      >
        {t("home.seeFullAgenda")}
        <ArrowUpRight size={14} />
      </Link>
    </div>
  );
}

/** Oficina sem horário, serviço ou profissional não recebe reserva —
 *  e o dono não tem como saber disso olhando a agenda vazia. */
function OnboardingBanner() {
  const t = useT();
  const { data } = useQuery({
    queryKey: ["onboarding"],
    queryFn: async () => (await api.get("/onboarding/")).data,
  });

  if (!data || data.complete) return null;

  const faltando = [
    !data.steps.hours.done && t("home.setupHours"),
    !data.steps.services.done && t("home.setupServices"),
    !data.steps.professionals.done && t("home.setupTeam"),
  ].filter(Boolean);

  return (
    <Card className="mt-4 border-amber-800/50 bg-amber-950/30 p-4">
      <p className="font-medium text-amber-300">{t("home.notAcceptingTitle")}</p>
      <p className="mt-1 text-sm text-amber-200/70">
        {t("home.notAcceptingText", { itens: faltando.join(", ") })}
      </p>
      <Link
        to="/comecar"
        className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-amber-300 hover:underline"
      >
        {t("home.finishSetup")} <ArrowUpRight size={14} />
      </Link>
    </Card>
  );
}

function Stat({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-steel-muted">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <p className="mt-2 text-3xl font-semibold">{value}</p>
      {hint && <p className="mt-1 text-xs text-steel-dim">{hint}</p>}
    </Card>
  );
}
